import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { canModifySettings } from "@/features/deployment-selection/model/permissions";
import { useAdminSettingsForm } from "@/features/deployment-settings/hooks/use-admin-settings-form";
import { DeploymentAreasSection } from "@/features/deployment-settings/components/deployment-areas-section";

import {
  type SettingsSectionId,
  SettingsSectionNav,
} from "@/features/deployment-settings/components/settings-section-nav";
import { ThresholdConfirmationDialog } from "@/features/deployment-settings/components/threshold-confirmation-dialog";
import { ThresholdConfigurationsSection } from "@/features/deployment-settings/components/threshold-configurations-section";

export function DeploymentSettingsPage() {
  const { selectedDeployment } = useSelectedDeployment();
  const settingsForm = useAdminSettingsForm();
  const [activeSection, setActiveSection] = useState<SettingsSectionId>("threshold-configs");
  const canEditSettings = canModifySettings(selectedDeployment);

  function handleSectionChange(section: SettingsSectionId) {
    setActiveSection(section);
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 1180, mx: "auto", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "flex-start" },
        }}
      >
        <Stack spacing={0.75}>
          <Typography component="h1" variant="h4">
            Deployment Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure alert thresholds and area capacity for {selectedDeployment.deployment_name}.
          </Typography>
        </Stack>
      </Box>

      {settingsForm.errorMessage != null && (
        <Alert severity="error" role="alert">
          {settingsForm.errorMessage}
        </Alert>
      )}

      {settingsForm.saveStatus === "saved" && (
        <Alert severity="success">
          Configuration saved. New values will be used for subsequent alert evaluations.
        </Alert>
      )}

      <Divider />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 3, md: 4 },
          alignItems: "flex-start",
        }}
      >
        <SettingsSectionNav activeSection={activeSection} onSectionChange={handleSectionChange} />
        <Stack spacing={5} sx={{ minWidth: 0, flex: 1, width: "100%" }}>
          {settingsForm.isLoading ? (
            <Stack spacing={1.5} sx={{ alignItems: "center", py: 8 }}>
              <CircularProgress size={28} />
              <Typography color="text.secondary">Loading deployment settings…</Typography>
            </Stack>
          ) : (
            <>
              {activeSection === "threshold-configs" && (
                <ThresholdConfigurationsSection configs={settingsForm.draft.thresholdConfigs} />
              )}
              {activeSection === "deployment-areas" && (
                <DeploymentAreasSection
                  areas={settingsForm.draft.areas}
                  canEdit={canEditSettings}
                  isEditing={settingsForm.isEditing}
                  isSaving={settingsForm.isSaving}
                  hasChanges={settingsForm.changes.length > 0}
                  validationError={settingsForm.validationError}
                  onStartEditing={settingsForm.startEditing}
                  onCancelEditing={settingsForm.cancelEditing}
                  onRequestSave={settingsForm.requestSave}
                  onChangeCapacity={settingsForm.updateAreaCapacity}
                />
              )}
            </>
          )}
        </Stack>
      </Box>

      <ThresholdConfirmationDialog
        open={settingsForm.isConfirmationOpen}
        deploymentName={selectedDeployment.deployment_name}
        isSaving={settingsForm.isSaving}
        changes={settingsForm.changes}
        onClose={settingsForm.closeConfirmation}
        onConfirm={() => void settingsForm.confirmSave()}
      />
    </Stack>
  );
}
